import React, { useEffect, useMemo, useState } from "react";
import { RocketIcon as Rocket, CheckCircleIcon as CheckCircle, WarningIcon as Warning, XCircleIcon as XCircle, InfoIcon as Info, ArrowsClockwiseIcon as ArrowsClockwise, ListChecksIcon as ListChecks, ShieldCheckIcon as ShieldCheck, EnvelopeSimpleIcon as EnvelopeSimple, GlobeHemisphereWestIcon as GlobeHemisphereWest, CreditCardIcon as CreditCard, LockIcon as Lock, DatabaseIcon as Database, BookOpenIcon as BookOpen, ChartLineUpIcon as ChartLineUp, ShieldStarIcon as ShieldStar, NotePencilIcon as NotePencil } from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RowListSkeleton } from "@/components/SkeletonList";
import { formatApiError } from "@/lib/utils-app";

/**
 * Admin · Launch readiness dashboard.
 *
 * Single-page green/yellow/red checklist. Backend probes live config +
 * database state on every request — refresh as often as you like.
 */

const STATUS_META = {
  green: { label: "Ready", tone: "#0F8043", bg: "#E8F5EE", icon: CheckCircle },
  yellow: { label: "Almost", tone: "#8A6A00", bg: "#FBF7EC", icon: Warning },
  red: { label: "Blocker", tone: "#C1272D", bg: "#FCE9EA", icon: XCircle },
};
const OVERALL_META = {
  ready: { label: "Ready to launch", tone: "#0F8043", bg: "#0F8043", description: "Every automated check is green. Confirm the manual items below and you're cleared for takeoff." },
  almost: { label: "Almost ready", tone: "#8A6A00", bg: "#8A6A00", description: "No hard blockers — clean up the yellow items, then re-run." },
  not_ready: { label: "Not ready to launch", tone: "#C1272D", bg: "#C1272D", description: "Hard blockers detected. Resolve every red item before going live." },
};

const CATEGORY_META = {
  Payments: { icon: CreditCard, accent: "#C9A227" },
  Email: { icon: EnvelopeSimple, accent: "#0E0E0E" },
  "Domain & Infra": { icon: GlobeHemisphereWest, accent: "#0E0E0E" },
  "Legal & Compliance": { icon: BookOpen, accent: "#C1272D" },
  Security: { icon: Lock, accent: "#0E0E0E" },
  "Analytics & Monitoring": { icon: ChartLineUp, accent: "#C9A227" },
  Insurance: { icon: ShieldStar, accent: "#0E0E0E" },
  "Data hygiene": { icon: Database, accent: "#6B6B6B" },
};
const CATEGORY_ORDER = ["Payments", "Email", "Domain & Infra", "Legal & Compliance", "Security", "Analytics & Monitoring", "Insurance", "Data hygiene"];
const PRIORITY_TONE = { P0: "#C1272D", P1: "#8A6A00", P2: "#3F4A8A" };

export default function AdminLaunchReadiness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [insuranceDialog, setInsuranceDialog] = useState(null);
  const [insuranceMeta, setInsuranceMeta] = useState(null);
  const [overrideDialog, setOverrideDialog] = useState(null);
  const [infoDialog, setInfoDialog] = useState(null);

  const refresh = async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get("/admin/launch-readiness");
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  const refreshInsuranceMeta = async () => {
    try {
      const r = await api.get("/admin/launch-readiness/insurance");
      setInsuranceMeta(r.data);
    } catch { /* not critical */ }
  };
  useEffect(() => { refresh(); refreshInsuranceMeta(); }, []);

  const grouped = useMemo(() => {
    const out = {};
    (data?.checks || []).forEach((c) => {
      out[c.category] = out[c.category] || [];
      out[c.category].push(c);
    });
    return out;
  }, [data]);

  const groupedInfo = useMemo(() => {
    const out = {};
    (data?.informational || []).forEach((c) => {
      out[c.category] = out[c.category] || [];
      out[c.category].push(c);
    });
    return out;
  }, [data]);

  const overall = data ? OVERALL_META[data.overall] : null;

  return (
    <AppShell>
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.2em] text-crimson font-bold flex items-center gap-1.5 mb-2">
          <Rocket size={12} weight="fill" /> Admin · Launch readiness
        </p>
        <h1 className="font-spectral text-4xl tracking-tight">Launch readiness</h1>
        <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
          Programmatic checklist of every blocker the registry can detect from
          live config + database state. Refresh any time — each probe runs
          fresh on every request.
        </p>
      </div>

      {loading && <RowListSkeleton />}

      {!loading && data && overall && (
        <>
          <div
            data-testid="launch-overall"
            className="rounded-2xl text-white p-5 sm:p-7 mb-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            style={{ background: overall.bg }}
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80 mb-1.5 flex items-center gap-1.5">
                <Rocket size={12} weight="fill" /> Overall status
              </p>
              <h2 className="font-spectral text-3xl tracking-tight">{overall.label}</h2>
              <p className="text-[14px] leading-relaxed text-white/85 mt-1.5 max-w-xl">
                {overall.description}
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-3 text-[12px] text-white/85">
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle size={13} weight="fill" /> {data.counts.green} green
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Warning size={13} weight="fill" /> {data.counts.yellow} yellow
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <XCircle size={13} weight="fill" /> {data.counts.red} red
                </span>
                <span className="text-white/60">·</span>
                <span>{data.total} automated check{data.total === 1 ? "" : "s"}</span>
              </div>
            </div>
            <Button
              onClick={() => refresh(true)}
              disabled={refreshing}
              data-testid="launch-refresh"
              className="rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/30"
            >
              <ArrowsClockwise size={14} weight="bold" className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Re-running…" : "Re-run checks"}
            </Button>
          </div>

          {CATEGORY_ORDER.filter((cat) => grouped[cat]?.length).map((cat) => {
            const meta = CATEGORY_META[cat] || { icon: ListChecks, accent: "#0E0E0E" };
            const Icon = meta.icon;
            return (
              <section key={cat} className="mb-7" data-testid={`launch-category-${cat.replace(/[^a-z]/gi, "_").toLowerCase()}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3 flex items-center gap-1.5" style={{ color: meta.accent }}>
                  <Icon size={12} weight="fill" /> {cat}
                </p>
                <ul className="space-y-2">
                  {grouped[cat].map((c) => (
                    <CheckRow
                      key={c.id}
                      check={c}
                      onEditInsurance={
                        c.category === "Insurance"
                          ? () => setInsuranceDialog(c.id.replace(/^insurance_/, ""))
                          : undefined
                      }
                      onOpenOverride={() => setOverrideDialog(c)}
                      onClearOverride={async () => {
                        try {
                          await api.delete(`/admin/launch-readiness/override/${c.id}`);
                          toast.success("Override cleared — automated verdict restored.");
                          refresh();
                        } catch (err) {
                          toast.error(formatApiError(err));
                        }
                      }}
                    />
                  ))}
                </ul>
              </section>
            );
          })}

          <section className="mt-12 mb-2 border-t border-[#E5E5E5] pt-8" data-testid="launch-informational">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#3F4A8A] font-bold flex items-center gap-1.5 mb-2">
              <Info size={12} weight="fill" /> Manual checklist
            </p>
            <h2 className="font-spectral text-2xl tracking-tight mb-2">
              Items we can't auto-detect
            </h2>
            <p className="text-sm text-[#6B6B6B] max-w-2xl mb-5">
              These can't be probed from the platform — they live with you,
              your lawyer, your domain registrar, or Stripe. Walk this list
              before launch and you're done.
            </p>
            {CATEGORY_ORDER.filter((cat) => groupedInfo[cat]?.length).map((cat) => {
              const meta = CATEGORY_META[cat] || { icon: ListChecks, accent: "#0E0E0E" };
              const Icon = meta.icon;
              return (
                <div key={cat} className="mb-5">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2 flex items-center gap-1.5" style={{ color: meta.accent }}>
                    <Icon size={11} weight="fill" /> {cat}
                  </p>
                  <ul className="space-y-2">
                    {groupedInfo[cat].map((it) => (
                      <InfoRow
                        key={it.id}
                        item={it}
                        onToggle={async (next) => {
                          try {
                            await api.put(`/admin/launch-readiness/informational/${it.id}`, {
                              completed: next,
                              note: it.note || "",
                            });
                            toast.success(next ? "Marked complete" : "Marked incomplete");
                            refresh();
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}
                        onEditNote={() => setInfoDialog(it)}
                      />
                    ))}
                  </ul>
                </div>
              );
            })}
          </section>
        </>
      )}

      <InsurancePolicyDialog
        policyId={insuranceDialog}
        meta={insuranceMeta}
        onClose={() => setInsuranceDialog(null)}
        onSaved={() => {
          setInsuranceDialog(null);
          refresh();
          refreshInsuranceMeta();
        }}
      />
      <OverrideDialog
        check={overrideDialog}
        onClose={() => setOverrideDialog(null)}
        onSaved={() => { setOverrideDialog(null); refresh(); }}
      />
      <InfoNoteDialog
        item={infoDialog}
        onClose={() => setInfoDialog(null)}
        onSaved={() => { setInfoDialog(null); refresh(); }}
      />
    </AppShell>
  );
}

function CheckRow({ check, onEditInsurance, onOpenOverride, onClearOverride }) {
  const meta = STATUS_META[check.status] || STATUS_META.yellow;
  const Icon = meta.icon;
  const isOverridden = !!check.overridden;
  return (
    <li
      data-testid={`launch-check-${check.id}`}
      className="rounded-xl border bg-white p-4 flex items-start gap-3"
      style={{ borderColor: meta.bg }}
    >
      <div
        className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center"
        style={{ background: meta.bg }}
      >
        <Icon size={18} weight="fill" style={{ color: meta.tone }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-ink">{check.label}</p>
          <span
            className="text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-full"
            style={{ background: meta.bg, color: meta.tone }}
            data-testid={`launch-check-status-${check.id}`}
          >
            {meta.label}
          </span>
          <span
            className="text-[10px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded border"
            style={{ color: PRIORITY_TONE[check.priority] || "#6B6B6B", borderColor: PRIORITY_TONE[check.priority] || "#6B6B6B" }}
          >
            {check.priority}
          </span>
          {isOverridden && (
            <span
              className="text-[10px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-full bg-[#FBF7EC] text-[#8A6A00] border border-[#E5D08A] inline-flex items-center gap-1"
              data-testid={`launch-check-overridden-${check.id}`}
              title={`Set by ${check.override?.updated_by_email || "admin"} on ${check.override?.updated_at?.slice(0, 10)}`}
            >
              <NotePencil size={10} weight="fill" /> Admin-set
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {onEditInsurance && (
              <button
                type="button"
                onClick={onEditInsurance}
                data-testid={`launch-edit-${check.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0F8043] hover:text-ink"
              >
                <NotePencil size={12} weight="bold" /> Update status
              </button>
            )}
            {isOverridden ? (
              <button
                type="button"
                onClick={onClearOverride}
                data-testid={`launch-clear-override-${check.id}`}
                className="text-[11px] font-bold text-[#6B6B6B] hover:text-crimson underline-offset-2 hover:underline"
              >
                Clear override
              </button>
            ) : (
              check.status !== "green" && onOpenOverride && (
                <button
                  type="button"
                  onClick={onOpenOverride}
                  data-testid={`launch-override-${check.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-[#8A6A00] hover:text-ink"
                >
                  <NotePencil size={12} weight="bold" /> Mark complete
                </button>
              )
            )}
          </div>
        </div>
        <p className="text-[13px] text-[#3F3F3F] leading-relaxed mt-1 whitespace-pre-wrap">
          {check.detail}
        </p>
        {isOverridden && check.original_status && (
          <p className="text-[11.5px] text-[#6B6B6B] italic mt-1.5">
            Automated probe reported: <strong>{(STATUS_META[check.original_status] || {}).label || check.original_status}</strong>
            {check.override?.reason && <> · Reason: <em>{check.override.reason}</em></>}
          </p>
        )}
        {check.action && (
          <p className="text-[12.5px] text-ink bg-[#FAFAFA] border-l-2 border-[#C9A227] pl-2 py-1 mt-2 whitespace-pre-wrap">
            <ShieldCheck size={11} weight="fill" className="inline mr-1 text-gold" />
            <strong>Action:</strong> {check.action}
          </p>
        )}
      </div>
    </li>
  );
}

/** ----------- InfoRow: manual-checklist item with a checkbox ----------- */
function InfoRow({ item, onToggle, onEditNote }) {
  const done = !!item.completed;
  return (
    <li
      data-testid={`launch-info-${item.id}`}
      className={`rounded-xl border p-3.5 flex items-start gap-3 ${
        done ? "bg-[#E8F5EE] border-[#9AD6B3]" : "bg-white border-[#E5E5E5]"
      }`}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        onClick={() => onToggle(!done)}
        data-testid={`launch-info-toggle-${item.id}`}
        className={`shrink-0 mt-0.5 h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors ${
          done
            ? "bg-[#0F8043] border-[#0F8043] text-white"
            : "border-[#C5C5C5] hover:border-[#0F8043]"
        }`}
      >
        {done && <CheckCircle size={13} weight="fill" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`font-semibold ${done ? "line-through text-[#6B6B6B]" : "text-ink"}`}>
            {item.label}
          </p>
          {done && item.completed_by_email && (
            <span className="text-[10px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-full bg-[#E8F5EE] text-[#0F8043]">
              ✓ {item.completed_by_email.split("@")[0]} · {(item.completed_at || "").slice(0, 10)}
            </span>
          )}
          <button
            type="button"
            onClick={onEditNote}
            className="ml-auto text-[11px] font-bold text-[#6B6B6B] hover:text-ink inline-flex items-center gap-1"
            data-testid={`launch-info-note-${item.id}`}
          >
            <NotePencil size={11} weight="bold" /> {item.note ? "Edit note" : "Add note"}
          </button>
        </div>
        <p className="text-[13px] text-[#3F3F3F] leading-relaxed mt-0.5">{item.guidance}</p>
        {item.note && (
          <p className="text-[12px] text-[#3a3a3a] italic mt-1.5 bg-white/60 border-l-2 border-[#C9A227] pl-2 py-1 whitespace-pre-wrap">
            {item.note}
          </p>
        )}
      </div>
    </li>
  );
}

/** ----------- OverrideDialog: flip a probe verdict manually ----------- */
function OverrideDialog({ check, onClose, onSaved }) {
  const [status, setStatus] = useState("green");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (check) { setStatus("green"); setReason(""); }
  }, [check]);
  if (!check) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/launch-readiness/override/${check.id}`, { status, reason });
      toast.success("Override saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!check} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="launch-override-dialog">
        <DialogHeader>
          <DialogTitle>Override: {check.label}</DialogTitle>
          <DialogDescription>
            Mark this check complete when the underlying requirement has been satisfied outside our system
            (e.g. DNS validated in Resend, backups enabled in Atlas). Recorded with your email + timestamp.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">Set status to</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="override-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="green">Ready (green)</SelectItem>
                <SelectItem value="yellow">Almost (yellow)</SelectItem>
                <SelectItem value="red">Blocker (red)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">Reason (audit trail)</label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. DNS validated in Resend dashboard at 10:32 ET"
              data-testid="override-reason"
            />
          </div>
          <p className="text-[11.5px] text-[#6B6B6B]">
            The automated probe will continue running every refresh — the override layers on top. Clear it any time
            with the &ldquo;Clear override&rdquo; button on the row.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="override-save">
            {busy ? "Saving…" : "Save override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** ----------- InfoNoteDialog: attach a note to a manual checklist item ----------- */
function InfoNoteDialog({ item, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (item) setNote(item.note || ""); }, [item]);
  if (!item) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/launch-readiness/informational/${item.id}`, {
        completed: !!item.completed,
        note,
      });
      toast.success("Note saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="launch-info-note-dialog">
        <DialogHeader>
          <DialogTitle>Note · {item.label}</DialogTitle>
          <DialogDescription>Capture the proof / receipt / policy number etc. so the next admin knows what was done.</DialogDescription>
        </DialogHeader>
        <Textarea
          rows={5}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Reviewed by Smith & Co LLP on 2026-02-12 · invoice #1428"
          data-testid="info-note-textarea"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="info-note-save">
            {busy ? "Saving…" : "Save note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InsurancePolicyDialog({ policyId, meta, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!policyId || !meta) { setForm(null); return; }
    const policy = (meta.policies || []).find((p) => p.id === policyId);
    if (!policy) { setForm(null); return; }
    setForm({
      status: policy.status || "not_started",
      carrier: policy.carrier || "",
      policy_number: policy.policy_number || "",
      limit: policy.limit || "",
      premium: policy.premium || "",
      expires_on: policy.expires_on || "",
      notes: policy.notes || "",
      label: policy.label,
      rationale: policy.rationale,
      typical_premium: policy.typical_premium,
    });
  }, [policyId, meta]);

  const open = !!policyId && !!form;

  const save = async () => {
    if (!form) return;
    setBusy(true);
    try {
      await api.put(`/admin/launch-readiness/insurance/${policyId}`, {
        status: form.status,
        carrier: form.carrier || undefined,
        policy_number: form.policy_number || undefined,
        limit: form.limit || undefined,
        premium: form.premium || undefined,
        expires_on: form.expires_on || undefined,
        notes: form.notes || undefined,
      });
      toast.success(`${form.label} updated`);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="insurance-dialog" className="max-w-lg">
        {form && (
          <>
            <DialogHeader>
              <DialogTitle className="font-spectral text-2xl tracking-tight flex items-center gap-2">
                <ShieldStar size={20} weight="duotone" className="text-[#0F8043]" />
                {form.label}
              </DialogTitle>
              <DialogDescription className="text-[13px] leading-relaxed">
                {form.rationale}
                <br /><br />
                <span className="text-[#8B8B8B]">Typical premium: {form.typical_premium}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] font-semibold text-ink mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="insurance-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta?.status_options || []).map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`insurance-status-option-${s.id}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[12px] font-semibold text-ink mb-1 block">Carrier</label>
                  <Input
                    value={form.carrier}
                    onChange={(e) => setForm({ ...form, carrier: e.target.value })}
                    placeholder="e.g. Hiscox"
                    data-testid="insurance-carrier"
                  />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-ink mb-1 block">Policy #</label>
                  <Input
                    value={form.policy_number}
                    onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                    placeholder="optional"
                    data-testid="insurance-policy-number"
                  />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-ink mb-1 block">Limit</label>
                  <Input
                    value={form.limit}
                    onChange={(e) => setForm({ ...form, limit: e.target.value })}
                    placeholder="e.g. $1M / $2M"
                    data-testid="insurance-limit"
                  />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-ink mb-1 block">Premium</label>
                  <Input
                    value={form.premium}
                    onChange={(e) => setForm({ ...form, premium: e.target.value })}
                    placeholder="e.g. $2,400/yr"
                    data-testid="insurance-premium"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[12px] font-semibold text-ink mb-1 block">Expires on</label>
                  <Input
                    type="date"
                    value={form.expires_on}
                    onChange={(e) => setForm({ ...form, expires_on: e.target.value })}
                    data-testid="insurance-expires"
                  />
                </div>
              </div>
              <div>
                <label className="text-[12px] font-semibold text-ink mb-1 block">Notes</label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  placeholder="Broker name, declination reasons, COI location, etc."
                  data-testid="insurance-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
              <Button
                onClick={save}
                disabled={busy}
                data-testid="insurance-save"
                className="rounded-full bg-[#0E0E0E] hover:bg-[#0F8043] text-white"
              >
                {busy ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
